/**
 * clusterConfig.js
 *
 * Factory for building `featureReduction` cluster configs. Pass a `type` to
 * get a different visual style — simple circle, count-labeled, or manual
 * pie-chart breakdown. Well-domain defaults (CURRENT_STATUS, STATUS_VALUES,
 * STATUS_COLORS, API_NUMBER) are baked in so callers can be one-liners —
 * every value is still override-able via options if reused for non-well data.
 *
 * Usage (minimal — uses all defaults):
 *   featureReduction: createClusterConfig({ type: 'pie' })
 *
 * Usage (override anything):
 *   featureReduction: createClusterConfig({
 *     type: 'pie',
 *     radius: 80,
 *     categoryField: 'SOME_OTHER_FIELD',
 *     categoryValues: ['A', 'B', 'C'],
 *   })
 */
import { STATUS_VALUES, STATUS_COLORS } from '@config/wellStatusConfig';
import UniqueValueRenderer from "@arcgis/core/renderers/UniqueValueRenderer.js";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol.js";
import * as clusterLabelCreator from "@arcgis/core/smartMapping/labels/clusters.js";
import * as pieChartRendererCreator from "@arcgis/core/smartMapping/renderers/pieChart.js";

/* -------------------------------------------------------------
   SHARED DEFAULTS
------------------------------------------------------------- */
const DEFAULTS = {
  radius: 60,
  minSize: 28,
  maxSize: 64,
  entityLabel: "wells",
  countField: "API_NUMBER",
  categoryField: "CURRENT_STATUS",
  categoryValues: STATUS_VALUES,
  categoryColors: STATUS_COLORS,
  defaultSliceColor: "#9FA8DA",
  shape: "donut",
  holePercentage: 0.66,
  pieSize: 44,
  simpleClusterColor: [24, 119, 242, 0.85],
  simpleOutlineColor: "#ffffff",
  simpleOutlineWidth: 1.5,
  labelColor: "#ffffff",
  labelHaloColor: "#000000",
  labelHaloSize: 0,
  labelFontSize: 10,
  labelFontWeight: "bold",
};

const defaultLabelingInfo = (expression = "$feature.cluster_count", overrides = {}) => ([{
  labelPlacement: "center-center",
  symbol: {
    type: "text",
    color: DEFAULTS.labelColor,
    haloColor: DEFAULTS.labelHaloColor,
    haloSize: DEFAULTS.labelHaloSize,
    font: { size: DEFAULTS.labelFontSize, weight: DEFAULTS.labelFontWeight },
  },
  labelExpressionInfo: { expression },
  deconflictionStrategy: "none",
  ...overrides,
}]);

/* -------------------------------------------------------------
   TYPE: "simple" — plain circle cluster, cluster_count label.
------------------------------------------------------------- */
function buildSimpleClusterConfig({
  radius = DEFAULTS.radius,
  minSize = DEFAULTS.minSize,
  maxSize = DEFAULTS.maxSize,
  color = DEFAULTS.simpleClusterColor,
  outlineColor = DEFAULTS.simpleOutlineColor,
  outlineWidth = DEFAULTS.simpleOutlineWidth,
  entityLabel = DEFAULTS.entityLabel,
  popupTitle = `Cluster: {cluster_count} ${DEFAULTS.entityLabel}`,
  popupText = "Zoom in or click to see individual wells.",
  labelingInfo,
} = {}) {
  return {
    type: "cluster",
    clusterRadius: radius,
    clusterMinSize: minSize,
    clusterMaxSize: maxSize,

    renderer: {
      type: "simple",
      symbol: {
        type: "simple-marker",
        style: "circle",
        color,
        size: 20,
        outline: { color: outlineColor, width: outlineWidth },
      },
    },

    labelingInfo: labelingInfo ?? defaultLabelingInfo("$feature.cluster_count"),

    popupTemplate: {
      title: popupTitle.includes("{cluster_count}") ? popupTitle : `Cluster: {cluster_count} ${entityLabel}`,
      content: [{ type: "text", text: popupText }],
    },
  };
}

/* -------------------------------------------------------------
   TYPE: "count" — labeled with a specific field's count.
   Defaults to counting API_NUMBER (wells).
------------------------------------------------------------- */
function buildCountClusterConfig({
  radius = "100px",
  minSize = "24px",
  maxSize = "60px",
  countField = DEFAULTS.countField,
  countFieldAlias,
  entityLabel = DEFAULTS.entityLabel,
  popupTitle = "Well Cluster",
} = {}) {
  const outName = `${countField}_COUNT`;
  const alias = countFieldAlias || outName;

  return {
    type: "cluster",
    clusterRadius: radius,
    clusterMinSize: minSize,
    clusterMaxSize: maxSize,

    fields: [{
      name: outName,
      alias,
      statisticType: "count",
      onStatisticField: countField,
    }],

    labelingInfo: defaultLabelingInfo(`$feature.${outName}`, {
      symbol: {
        type: "text",
        color: "white",
        font: { size: "12px", weight: "bold" },
      },
    }),

    popupTemplate: {
      title: popupTitle,
      content: `This cluster contains {${outName}} ${entityLabel}.`,
      fieldInfos: [{
        fieldName: outName,
        format: { places: 0, digitSeparator: true },
      }],
    },
  };
}

/* -------------------------------------------------------------
   TYPE: "smart-pie" — Esri smart-mapping pie-chart cluster renderer.
   ASYNC (unlike the other types) since createRendererForClustering()
   must run against a live layer/view.

   CONFIRMED WORKING pattern (verified end-to-end in PieMapWell):
   - Real class instances required: UniqueValueRenderer + SimpleMarkerSymbol
     (NOT plain object literals, NOT SimpleFillSymbol — wells are POINTS).
   - No grouping via Arcade/valueExpression/uniqueValueGroups — just pick
     ≤10 raw category values directly (confirmed working set uses 9), give
     each its own uniqueValueInfo, and let defaultSymbol/defaultLabel:"Other"
     absorb the rest.
   - layer.renderer must be set to this clean renderer BEFORE calling
     createRendererForClustering.
   - fieldName IS still passed to createRendererForClustering alongside
     the pre-set layer.renderer (both matter).

   `categories` shape matches the confirmed working config exactly:
     [{ value, label, color }, ...]  — max 10 entries.
   Remaining raw field values not listed fall into "Other" via defaultSymbol
   automatically — they do NOT need to be enumerated.
------------------------------------------------------------- */

// Confirmed-working default category set for CURRENT_STATUS (9 entries,
// leaves 1 slot of headroom under the 10-cap). Matches arcgisConfig.well
// from the verified-working PieMapWell test.
const DEFAULT_SMART_PIE_CATEGORIES = [
  { value: "GAS",     label: "Active Gas",              color: "#1976D2" },
  { value: "OIL",     label: "Active Oil",               color: "#2E7D32" },
  { value: "CBM",     label: "Active Coalbed Methane",   color: "#00897B" },
  { value: "INJ",     label: "Active Injection",         color: "#00ACC1" },
  { value: "O&I",     label: "Active Oil & Injection",   color: "#43A047" },
  { value: "INA-GAS", label: "Inactive Gas",             color: "#F9A825" },
  { value: "INA-OIL", label: "Inactive Oil",             color: "#FB8C00" },
  { value: "ABD-GAS", label: "Plugged Gas",              color: "#C62828" },
  { value: "D&A",     label: "Plugged D&A",              color: "#6D4C41" },
];

async function buildSmartPieClusterConfig({
  layer,
  view,
  categoryField = DEFAULTS.categoryField,
  categories = DEFAULT_SMART_PIE_CATEGORIES, // [{ value, label, color }]
  otherLabel = "Other",
  otherColor = "#808080",
  shape = DEFAULTS.shape,
  holePercentage = DEFAULTS.holePercentage,
  entityLabel = DEFAULTS.entityLabel,
  popupTitle = "Well Cluster",
} = {}) {
  if (!layer || !view) {
    throw new Error("[clusterConfig] type:'smart-pie' requires { layer, view }.");
  }
  if (!categories?.length) {
    throw new Error("[clusterConfig] type:'smart-pie' requires categories (max 10, [{value,label,color}]).");
  }
  if (categories.length > 10) {
    console.warn(
      `[clusterConfig] smart-pie categories has ${categories.length} entries — ` +
      `capping at 10 (the SDK's hard limit). Extra entries will be dropped, not grouped.`
    );
    categories = categories.slice(0, 10);
  }

  await layer.load();

  // 1. Marker symbol helper — points, not polygons (confirmed: SimpleMarkerSymbol,
  //    NOT simple-fill, since wells are point geometries).
  const createMarkerSymbol = (color) => new SimpleMarkerSymbol({
    size: 6,
    color,
    outline: { color: "white", width: 0.5 },
  });

  // 2. Real UniqueValueRenderer instance — flat uniqueValueInfos, one per
  //    raw category value, decoupled label/color. No grouping, no valueExpression.
  const uniqueValueRenderer = new UniqueValueRenderer({
    field: categoryField,
    defaultLabel: otherLabel,
    defaultSymbol: createMarkerSymbol(otherColor),
    uniqueValueInfos: categories.map(({ value, label, color }) => ({
      value,
      label: label ?? value,
      symbol: createMarkerSymbol(color),
    })),
  });

  // 🔑 Must be set BEFORE calling createRendererForClustering
  layer.renderer = uniqueValueRenderer;

  // 3. Cluster label scheme
  const labelSchemes = await clusterLabelCreator.getLabelSchemes({ layer, view });
  const { labelingInfo, clusterMinSize } = labelSchemes.primaryScheme;
  const labelSymbol = labelingInfo?.[0]?.symbol;
  if (labelSymbol?.haloColor) {
    labelSymbol.color = labelSymbol.haloColor.clone();
    labelSymbol.haloColor = [255, 255, 255, 0.3];
  }
  if (labelSymbol?.font) labelSymbol.font.size = 10;

  // 4. Pie-chart cluster renderer — fieldName passed alongside the
  //    pre-set layer.renderer (both matter, confirmed).
  const { renderer, fields } = await pieChartRendererCreator.createRendererForClustering({
    layer,
    view,
    fieldName: categoryField,
    shape,
  });
  renderer.holePercentage = holePercentage;

  // 5. Popup
  const fieldInfos = fields.map((field) => ({
    fieldName: field.name,
    label: field.alias,
    format: { places: 0, digitSeparator: true },
  }));
  const fieldNames = fieldInfos.map((f) => f.fieldName);

  const popupTemplate = {
    title: popupTitle,
    content: [
      { type: "text", text: `This cluster represents <b>{cluster_count}</b> ${entityLabel}.` },
      {
        type: "media",
        mediaInfos: [{
          title: `${categoryField} Distribution`,
          type: "pie-chart",
          value: { fields: fieldNames },
        }],
      },
      { type: "fields" },
    ],
    fieldInfos,
  };

  return {
    type: "cluster",
    popupTemplate,
    labelingInfo,
    clusterMinSize,
    fields,
    renderer,
  };
}
/**
 * @param {Object} options
 * @param {"simple"|"count"|"pie"|"smart-pie"} [options.type="simple"]
 * @param {...*} options - forwarded to the matching builder; any well-domain
 *   default above can be overridden per-call.
 * @returns {Object|Promise<Object>} a plain featureReduction config object.
 *   NOTE: type:"smart-pie" is ASYNC (returns a Promise, requires
 *   {layer, view}) — await it before assigning to layer.featureReduction.
 *   All other types are synchronous.
 */
export function createClusterConfig({ type = "simple", ...rest } = {}) {
  switch (type) {
    case "count":
      return buildCountClusterConfig(rest);
    case "smart-pie":
      return buildSmartPieClusterConfig(rest); // returns a Promise — await it
    case "simple":
    default:
      return buildSimpleClusterConfig(rest);
  }
}

// Exported in case a caller wants the raw defaults for inspection/testing.
export const CLUSTER_CONFIG_DEFAULTS = DEFAULTS;